import { useMemo } from "react";
import { components, paths } from "@core/api/api";
import useAxios from "@hooks/useFetch";
import { CommonDataKeywordItem } from "./useCommonData";
import { useCommonDataContext } from "@core/CommonDataContext";

const HISTORIC_TREND_ENDPOINT: keyof paths = '/api/Trend/GetHistoricTrend';

export type HistoricTrendRequest = components["schemas"]["FlavorTracker.Entities.DTO.TrendPage.TrendPageRequest"];
export type HistoricTrendResponseItem = components["schemas"]["FlavorTracker.Entities.DTO.TrendPage.HistoricTrendData"];
export type HistoricTrendResponseRaw = HistoricTrendResponseItem[];
export type HistoricTrendItem = {
    id: HistoricTrendResponseItem["id"];
    name: CommonDataKeywordItem["name"];
    changeValue: string;
    changeValueSigned: string;
    changeNum: number | null;
    averageValue: string;
    averageNum: number | null;
    surveys: Map<number, {
        surveyId: number;
        valueRaw: number | null;
        value: string;
        valueNum: number | null;
    }>;
};
export type HistoricTrendTableItem = HistoricTrendItem & { [key: number]: number | null };

export type HistoricTrendResponse = Record<number, HistoricTrendItem>;

export function useHistoricTrend(payload: HistoricTrendRequest): {
    isLoading: boolean;
    data: HistoricTrendResponse;
} {
    const response = useAxios<HistoricTrendResponseRaw, HistoricTrendRequest>({
        url: HISTORIC_TREND_ENDPOINT,
        method: 'post',
        payload,
    });

    const { keywords } = useCommonDataContext();

    const data = useMemo(() => {
        const data: HistoricTrendResponse = {};
        (response.data ?? []).forEach((item) => {
            const id = item.id;
            if (!id) return;

            const change = item.change ?? null;
            const changeSign = (item.change ?? 0) > 0 ? '+' : '';
            const average = item.average ?? null;

            const surveys: HistoricTrendTableItem["surveys"] = new Map();

            item.dataBySurvey?.forEach(i => {
                surveys.set(i.surveyId ?? 0, {
                    surveyId: i.surveyId ?? 0,
                    valueRaw: i.raw ?? null,
                    value: i.raw === null ? '-' : `${i.raw}%`,
                    valueNum: i.originalRaw ?? null,
                });
            });

            data[id] = {
                id,
                name: keywords?.[id]?.name ?? '',
                changeValue: change === null ? '-' : `${change}%`,
                changeValueSigned: change === null ? '-' : `${changeSign}${change}%`,
                changeNum: item.originalChange ?? null,
                averageValue: average === null ? '-' : `${average}%`,
                averageNum: item.originalAverage ?? null,
                surveys: surveys,
            };
        });

        return data;
    }, [response.data, keywords]);

    return {
        ...response,
        data,
    };
}